import { FacebookComment } from '@/types/fb-type';
import { useDeviceType } from '@/hooks/use-device-type';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  getLiveCommentsPanelClassName,
  LiveCommentsSizePreset,
  nextPanelSize,
} from './live-comments-panel-layout';

type UseLiveCommentsFloatingWidgetOptions = {
  comments: FacebookComment[];
  open: boolean;
  initialSize?: LiveCommentsSizePreset;
  isControlled: boolean;
  onOpenChange?: (open: boolean) => void;
};

export function useLiveCommentsFloatingWidget({
  comments,
  open,
  initialSize = 'lg',
  isControlled,
  onOpenChange,
}: UseLiveCommentsFloatingWidgetOptions) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [size, setSize] = useState<LiveCommentsSizePreset>(initialSize);
  const [unread, setUnread] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewBanner, setShowNewBanner] = useState(false);

  const lastCountRef = useRef(comments.length);
  const prevCountRef = useRef(comments.length);

  const { isMobile, isDesktop } = useDeviceType();
  const isOpen = isControlled ? open : internalOpen;

  useEffect(() => {
    if (isMobile) {
      setSize((current) => (current === 'lg' || current === 'md' ? 'xs' : current));
    }
  }, [isMobile]);

  const handleOpenChange = (next: boolean) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };

  useEffect(() => {
    const curr = comments.length;
    const prev = lastCountRef.current;
    if (!isOpen && curr > prev) {
      setUnread((count) => count + (curr - prev));
    }
    lastCountRef.current = curr;
  }, [comments.length, isOpen]);

  useEffect(() => {
    if (isOpen && unread > 0) setUnread(0);
  }, [isOpen, unread]);

  useEffect(() => {
    const curr = comments.length;
    const prev = prevCountRef.current;
    if (isOpen && curr > prev) {
      setShowNewBanner(true);
      prevCountRef.current = curr;
      const timer = setTimeout(() => setShowNewBanner(false), 2000);
      return () => clearTimeout(timer);
    }
    prevCountRef.current = curr;
  }, [comments.length, isOpen]);

  const filteredComments = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();
    if (!term) return comments;
    return comments.filter(
      (comment) =>
        comment.fromName?.toLowerCase().includes(term) ||
        comment.message?.toLowerCase().includes(term),
    );
  }, [comments, searchQuery]);

  const panelClassName = useMemo(
    () => getLiveCommentsPanelClassName({ isMobile, isDesktop, size }),
    [isMobile, isDesktop, size],
  );

  const toggleSize = () => setSize((current) => nextPanelSize(current));

  return {
    isMobile,
    isOpen,
    size,
    setSize,
    toggleSize,
    unread,
    searchQuery,
    setSearchQuery,
    showNewBanner,
    filteredComments,
    panelClassName,
    handleOpenChange,
  };
}
